import React, { useState } from "react";
import { UserDetailsProps } from "src/services/types";
import Modal from "./Modal";
import AdminService from "../../../services/admin.services";
import CreateSuspension from "src/pages/settings/components/CreateSuspension";
import ConfirmationModal from "src/components/ui/ConfirmationModal";
import {
  FiMail,
  FiPhone,
  FiClock,
  FiShield,
  FiToggleLeft,
  FiToggleRight,
  FiX,
  FiMapPin,
} from "react-icons/fi";
import { FaCopy } from "react-icons/fa";

const adminService = new AdminService();

const UserDetails: React.FC<UserDetailsProps> = ({
  user,
  onStatusChange,
  addresses = [],
  addressLoading = false,
  addressError,
}) => {
  const [status, setStatus] = useState(
    user.is_suspended ? "suspended" : "active"
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: "",
    message: "",
    type: "success" as "success" | "error",
    action: () => {},
  });
  const [showSuspensionModal, setShowSuspensionModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isSuspending, setIsSuspending] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(user._id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleToggleStatus = () => {
    setShowDeactivateModal(true);
  };

  const confirmToggleStatus = async () => {
    setIsDeactivating(true);
    try {
      const response = await adminService.checkUser({ email: user.email });
      const newStatus = status === "active" ? "inactive" : "active";
      const actionMessage =
        newStatus === "active" ? "activated" : "deactivated";

      setModalContent({
        title: `${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)} User`,
        message:
          response.success ||
          `User ${user.first_name} ${user.last_name} ${actionMessage} successfully.`,
        type: "success",
        action: () => {
          setStatus(newStatus);
          onStatusChange(newStatus);
          setIsModalOpen(false);
        },
      });
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error toggling user status:", error);
      setModalContent({
        title: "Error",
        message: `Failed to ${
          status === "active" ? "deactivate" : "activate"
        } user. ${error instanceof Error ? error.message : ""}`,
        type: "error",
        action: () => setIsModalOpen(false),
      });
      setIsModalOpen(true);
    } finally {
      setShowDeactivateModal(false);
      setIsDeactivating(false);
    }
  };

  const handleSuspension = async () => {
    if (user.is_suspended) {
      try {
        const response = await adminService.updateSuspensionStatus(user._id);
        setModalContent({
          title: response?.success ? "Suspension Removed" : "Error",
          message: response?.success
            ? "User suspension removed successfully."
            : "Failed to update suspension status.",
          type: response?.success ? "success" : "error",
          action: () => {
            setStatus("active");
            onStatusChange("active");
            setIsModalOpen(false);
          },
        });
        setIsModalOpen(true);
      } catch (error) {
        setModalContent({
          title: "Error",
          message: "An error occurred while updating suspension status.",
          type: "error",
          action: () => setIsModalOpen(false),
        });
        setIsModalOpen(true);
      }
    } else {
      setShowSuspendModal(true);
    }
  };

  const confirmSuspendUser = () => {
    setIsSuspending(true);
    setShowSuspendModal(false);
    setShowSuspensionModal(true);
    setIsSuspending(false);
  };

  const getStatusColor = () => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "suspended":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatAddress = (address: any): string => {
    if (!address || typeof address.state !== "string") {
      return "No saved address";
    }

    const state = address.state.trim();
    if (!state.length || state.toLowerCase() === "unknown") {
      return "No saved address";
    }

    return state;
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
      {/* User ID Section */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
            <span>User ID:</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="px-3 py-1.5 bg-white rounded-md text-gray-700 text-sm truncate max-w-xs">
              {user._id}
            </div>
            <button
              onClick={handleCopy}
              className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
              aria-label="Copy User ID"
            >
              <FaCopy className="w-4 h-4" />
            </button>
          </div>
        </div>
        {copied && (
          <div className="text-xs text-green-500 mt-1 text-right animate-fade-in">
            Copied to clipboard!
          </div>
        )}
      </div>

      {/* Profile Header */}
      <div className="relative h-48 bg-gradient-to-r from-blue-500 to-purple-600">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center p-6 text-white">
            <h1 className="text-2xl font-bold">
              {user.first_name} {user.last_name}
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 md:p-8">
        {/* Profile Section */}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-8">
          <div className="relative -mt-20">
            <img
              src={user.image_url || "/default-avatar.png"}
              alt={`${user.first_name} ${user.last_name}`}
              className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
            />
            <span
              className={`absolute bottom-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor()}`}
            >
              {status === "active" ? "Active" : "Suspended"}
            </span>
          </div>

          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold text-gray-800">
              {user.first_name} {user.last_name}
            </h2>
            <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-4">
              <div className="flex items-center text-gray-600">
                <FiMail className="mr-2" />
                <span>{user.email}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <FiPhone className="mr-2" />
                <span>{user.phone || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <DetailCard
            icon={<FiClock className="w-5 h-5" />}
            title="Last Login"
            value={
              user.last_login
                ? new Date(user.last_login).toLocaleString()
                : "N/A"
            }
          />
          <DetailCard
            icon={<FiShield className="w-5 h-5" />}
            title="Account Status"
            value={status === "active" ? "Active" : "Suspended"}
          />
          <DetailCard
            icon={<FiMapPin className="w-5 h-5" />}
            title="Primary Address"
            value={
              addressLoading
                ? "Fetching address..."
                : addressError
                ? addressError
                : addresses.length
                ? formatAddress(addresses[0])
                : "No saved addresses"
            }
          />
        </div>

        {/* Address List */}
        <div className="bg-gray-50 p-5 rounded-lg mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600">
              <FiMapPin className="w-5 h-5" />
            </span>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                Saved Delivery Addresses
              </h3>
              <p className="text-sm text-gray-500">
                Addresses associated with this user account.
              </p>
            </div>
          </div>

          {addressLoading ? (
            <p className="text-gray-500 text-sm">Fetching addresses...</p>
          ) : addressError ? (
            <p className="text-red-500 text-sm">{addressError}</p>
          ) : addresses.length ? (
            <ul className="space-y-3">
              {addresses.map((addressItem, index) => (
                <li
                  key={addressItem?._id || `address-${index}`}
                  className="bg-white border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-700">
                      {addressItem?.label ||
                        addressItem?.type ||
                        `Address ${index + 1}`}
                    </span>
                    {addressItem?.is_default && (
                      <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    {formatAddress(addressItem)}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-sm">No saved addresses.</p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleToggleStatus}
            className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              status === "active"
                ? "bg-red-100 text-red-700 hover:bg-red-200"
                : "bg-green-100 text-green-700 hover:bg-green-200"
            }`}
          >
            {status === "active" ? (
              <>
                <FiToggleLeft className="w-5 h-5" />
                Deactivate
              </>
            ) : (
              <>
                <FiToggleRight className="w-5 h-5" />
                Activate
              </>
            )}
          </button>

          <button
            onClick={handleSuspension}
            className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              user.is_suspended
                ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                : "bg-red-100 text-red-700 hover:bg-red-200"
            }`}
          >
            {user.is_suspended ? "Remove Suspension" : "Suspend User"}
          </button>
        </div>
      </div>

      {/* Modals */}
      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        onConfirm={confirmToggleStatus}
        title={status === "active" ? "Deactivate User" : "Activate User"}
        message={
          status === "active"
            ? "Are you sure you want to deactivate this user? They will no longer be able to access the platform."
            : "Are you sure you want to activate this user? They will regain access to the platform."
        }
        confirmText={status === "active" ? "Deactivate" : "Activate"}
        cancelText="Cancel"
        type="danger"
        entityName={`${user.first_name} ${user.last_name}`}
        entityType="user"
        isLoading={isDeactivating}
        loadingText={status === "active" ? "Deactivating..." : "Activating..."}
      />

      <ConfirmationModal
        isOpen={showSuspendModal}
        onClose={() => setShowSuspendModal(false)}
        onConfirm={confirmSuspendUser}
        title="Suspend User"
        message="Are you sure you want to suspend this user? They will be temporarily blocked from accessing the platform."
        confirmText="Suspend"
        cancelText="Cancel"
        type="warning"
        entityName={`${user.first_name} ${user.last_name}`}
        entityType="user"
        isLoading={isSuspending}
        loadingText="Processing..."
      />

      <Modal
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalContent.title}
        message={modalContent.message}
        type={modalContent.type}
      />

      {showSuspensionModal && (
        <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full relative">
            <button
              onClick={() => setShowSuspensionModal(false)}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100"
            >
              <FiX className="w-5 h-5 text-gray-500" />
            </button>
            <CreateSuspension
              suspendeeId={user._id}
              type="user"
              onClose={() => setShowSuspensionModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component for detail cards
const DetailCard = ({
  icon,
  title,
  value,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
}) => (
  <div className="bg-gray-50 p-4 rounded-lg">
    <div className="flex items-center gap-3 mb-2">
      <div className="text-gray-500">{icon}</div>
      <h3 className="font-medium text-gray-700">{title}</h3>
    </div>
    <p className="text-gray-900 pl-8">{value}</p>
  </div>
);

export default UserDetails;
